import { VariantReader } from "aldoo-ra/VariantReader"
import Spinner from "aldoo-ra/Spinner"
import Tooltip from "aldoo-ra/Tooltip"
import { twMerge } from "tailwind-merge"
import { createContext, useContext, useState } from "react"

/**
 * How a link's `to` becomes the href the page navigates to. An app whose
 * routes carry something the component cannot know — a language prefix, a
 * base path — provides its own resolver once, at its root:
 *
 *   <LinkProvider resolve={(url) => `/${lang}${url}`}>…</LinkProvider>
 *
 * Only site-relative hrefs ("/x", not "//host/x" or "https://…") go through it.
 */
const LinkContext = createContext((url) => url)
export const LinkProvider = ({ resolve, children }) => <LinkContext.Provider value={resolve}>{children}</LinkContext.Provider>

const isSiteUrl = (url) => typeof url === "string" && url.startsWith("/") && !url.startsWith("//")

/**
 * A button, or — given `href` — a link that looks like one. A site-relative
 * href ("/x") goes through the app's LinkProvider resolver; any other url is
 * used as is. `variant="none"` (the default) renders with no styling of its
 * own, for a link that is an image, a card, a word in a sentence.
 */
export default function Button({
  variant = "none",
  styleOverrides,
  loading,
  children,
  onClick,
  disabled,
  className = "",
  confirm = false,
  confirmMessage = "Are you sure?", // Can be string or JSX/component
  "aria-label": ariaLabel,
  type,
  href,
  target,
  rel,
  title,
  style,
  ...rest
}) {
  // data-* / aria-* attributes (test ids, describedby…) reach the element
  const passThrough = Object.fromEntries(Object.entries(rest).filter(([key]) => key.startsWith("data-") || key.startsWith("aria-")))
  const [showConfirm, setShowConfirm] = useState(false)
  const resolve = useContext(LinkContext)

  const variantClasses = VariantReader.useVariant({
    componentName: "Button",
    variant,
    styleOverrides,
  })
  const variants = variant === "none" ? "" : variantClasses

  // a link: the anchor the router intercepts, with the app's own url shape
  const url = href
  if (url !== undefined) {
    const resolved = isSiteUrl(url) ? resolve(url) : url
    const external = /^[a-z]+:/i.test(url) || url.startsWith("//")
    return (
      <a
        href={disabled ? undefined : resolved}
        target={target ?? (external ? "_blank" : undefined)}
        rel={rel ?? (external ? "noopener noreferrer" : undefined)}
        title={title}
        style={style}
        aria-label={ariaLabel}
        aria-disabled={disabled || undefined}
        onClick={disabled ? (e) => e.preventDefault() : onClick}
        className={twMerge(variants, className)}
        {...passThrough}
      >
        {children}
      </a>
    )
  }

  const handleClick = (e) => {
    if (confirm && !showConfirm) {
      setShowConfirm(true)
      return
    }

    setShowConfirm(false)
    onClick?.(e)
  }

  const handleBlur = () => {
    if (showConfirm) {
      setShowConfirm(false)
    }
  }

  const handleMouseLeave = () => {
    if (showConfirm) {
      setShowConfirm(false)
    }
  }

  const buttonContent = (
    <button
      type={type}
      {...passThrough}
      disabled={disabled || loading}
      onClick={handleClick}
      onBlur={handleBlur}
      onMouseLeave={handleMouseLeave}
      className={twMerge(variants, className)}
      aria-label={ariaLabel}
      title={title}
      style={style}
    >
      {loading ? (
        <div className="flex items-center justify-center">
          <span className="invisible">{children}</span>
          <Spinner variant="button" className="absolute" />
        </div>
      ) : (
        showConfirm ? "Confirm" : children
      )}
    </button>
  )

  if (confirm) {
    return (
      <Tooltip
        content={
          <div className="bg-admin_primary rounded-lg px-4 py-3 shadow-lg">
            <div className="text-white font-medium">
              {confirmMessage}
            </div>
          </div>
        }
        position="top"
        display="inline-flex"
        isOpen={showConfirm}
        variant="none"
      >
        {buttonContent}
      </Tooltip>
    )
  }

  return buttonContent
}
